import {useMutation, useQuery} from '@tanstack/react-query';
import {growthbookReadyAtom} from 'atoms/layout';
import {VisitService, WorkstreamService} from 'client';
import {extractIdFromSlug} from 'helpers/utils';
import {useAtomValue} from 'jotai';
import {useEffect, useMemo} from 'react';
import {useLocation, useSearchParams} from 'react-router';
import {getService, isCollabServerEnabledForPage} from 'utilities';
import {parseWorkstreamsPlace} from 'views/workstreams/page/workstreamsRoutes';

// store last visit times per key (in ms)
const lastVisitMap = new Map<string, number>();

export function useRecordVisit() {
  const key = usePresencePageKey();
  const {mutate} = useMutation({
    mutationFn: getService(VisitService).visitApiV1VisitsPost,
  });

  useEffect(() => {
    if (!key) return;

    const now = Date.now();
    // const last = lastVisitMap.get(key) ?? 0;

    // Only record a visit to that key if it's been at least 60 seconds
    // if (now - last >= 60_000) {

    // only report for workstreams and editor
    if (isCollabServerEnabledForPage(key)) {
      lastVisitMap.set(key, now);
      mutate(key);
    }

    // }
  }, [key, mutate]);
}

export const usePageVisitors = () => {
  const key = usePresencePageKey();

  const {data, isLoading} = useQuery({
    enabled: !!key && isCollabServerEnabledForPage(key),
    queryKey: ['presence', key],
    queryFn: () => getService(VisitService).getApiV1VisitsPageKeyGet(key ?? ''),
  });

  return {visitors: data, isVisitorsLoading: isLoading};
};

export const RESTRICTED_WORKSTREAM_NAME = 'restricted';

export const isRestrictedWorkstreamPresenceResponse = (
  workstream: {name?: string | null; requested?: boolean | null} | undefined
) => {
  if (!workstream) return false;
  if (workstream.requested === null || workstream.requested === undefined) return false;

  return workstream.name?.trim().toLowerCase() === RESTRICTED_WORKSTREAM_NAME;
};

export const getWorkstreamPresenceAccessCheckId = (pathname: string): number | string | null => {
  if (
    pathname.startsWith('/workstreams/checkup') ||
    pathname.startsWith('/workstreams/exec-sponsor-inbox') ||
    pathname.startsWith('/workstreams-old/exec-sponsor-inbox')
  ) {
    return null;
  }

  const parts = pathname.replace(/\/+$/, '').split('/').filter(Boolean);
  const [label, id] = parts;

  // TEMPORARY: 'workstreams-old' is the previous page, kept a day after the swap.
  if (!id || (label !== 'workstreams' && label !== 'workstreams-old')) {
    return null;
  }
  // The page's rail places aren't workstreams.
  if (label === 'workstreams' && parseWorkstreamsPlace(id)) {
    return null;
  }

  const parsedId = parseInt(id, 10);
  return Number.isNaN(parsedId) ? id : parsedId;
};

export const usePresencePageKey = () => {
  const {pathname} = useLocation();
  const [searchParams] = useSearchParams();
  const isGrowthbookReady = useAtomValue(growthbookReadyAtom);
  const accessCheckId = getWorkstreamPresenceAccessCheckId(pathname);

  const {data: workstream, isLoading} = useQuery({
    queryKey: ['workstream', accessCheckId, undefined, undefined],
    queryFn: () => getService(WorkstreamService).getApiV1WorkstreamIdGet(accessCheckId!),
    enabled: isGrowthbookReady && accessCheckId !== null,
  });

  return useMemo(() => {
    if (accessCheckId !== null) {
      if (isLoading || !workstream || isRestrictedWorkstreamPresenceResponse(workstream)) {
        return undefined;
      }
    }

    return pageKeyForPath(pathname, searchParams);
  }, [accessCheckId, isLoading, pathname, searchParams, workstream]);
};

const ID_LIKE = /^(?:\d+|[0-9a-fA-F-]{8,}|[A-Za-z0-9_-]{3,})$/; // tweak as needed
const GROUP_SUFFIXES = new Set(['edit', 'activity', 'checkup']);

export function pageKeyForPath(pathname: string, searchParams: URLSearchParams) {
  const parts = pathname.replace(/\/+$/, '').split('/').filter(Boolean);
  if (parts.length === 0) return 'home';

  const [label, ...rest] = parts;

  // Special case for workstream views routes - include query params in the pageKey
  if (label === 'workstreams') {
    // A workstream's full page (/workstreams/:id/detail) shares its card's key
    // (/workstreams/:id, the old page's URL too), so its face pile shows everyone
    // who has looked at the workstream.
    if (rest.length === 2 && /^\d+$/.test(rest[0]) && rest[1] === 'detail') rest.pop();
    const view = searchParams.get('view');
    const scope = searchParams.get('scope');
    const value = searchParams.get('value');

    const queryParts: string[] = [];
    if (view) queryParts.push(`view:${view}`);
    if (scope) queryParts.push(`scope:${scope}`);
    if (value) queryParts.push(`value:${value}`);

    if (queryParts.length > 0) {
      return rest.length
        ? `${label}:${rest.join(':')}:${queryParts.join(':')}`
        : `${label}:${queryParts.join(':')}`;
    }
    // No query params
    return rest.length ? `${label}:${rest.join(':')}` : label;
  }

  // Special case for planning routes - extract ID from slug
  if (label === 'planning' && rest.length > 0) {
    const slug = rest[0];
    const id = extractIdFromSlug(slug);
    const suffix = rest.slice(1).filter(Boolean);
    return suffix.length ? `${label}:${id}:${suffix.join(':')}` : `${label}:${id}`;
  }

  // If shape is label/id/(edit|activity) → group to label:id
  if (rest.length >= 2 && ID_LIKE.test(rest[0]) && GROUP_SUFFIXES.has(rest[1])) {
    return `${label}:${rest[0]}`;
  }

  // Generic: if first ID-like segment, include it + any remaining static bits
  const idIdx = rest.findIndex((seg) => ID_LIKE.test(seg));
  if (idIdx >= 0) {
    const id = rest[idIdx];
    const suffix = rest.slice(idIdx + 1).filter(Boolean);
    return suffix.length ? `${label}:${id}:${suffix.join(':')}` : `${label}:${id}`;
  }

  // No ID found → join statics
  return rest.length ? `${label}:${rest.join(':')}` : label;
}
